import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import SEOHead from '@/components/SEOHead';
import { ChatWidget } from '@/components/ChatWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TermCard({ term }) {
  var related = term.related_terms || [];
  var relatedItems = related.slice(0, 3).map(function (rt) {
    return (
      <span
        key={rt}
        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
      >
        {rt}
      </span>
    );
  });

  return (
    <Link
      to={'/solutions/' + term.slug}
      className="group bg-white border border-gray-200 hover:border-[#81bce3] rounded-xl p-5 transition-all hover:shadow-lg"
      data-testid={'glossary-term-' + term.slug}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-bold text-gray-900 group-hover:text-[#2C39B8] transition-colors">
          {term.term}
        </h3>
        <ArrowRight
          size={16}
          className="text-gray-400 group-hover:text-[#2C39B8] flex-shrink-0 mt-1 transition-colors"
        />
      </div>
      <p className="text-sm text-gray-600 line-clamp-3">
        {term.definition || term.meta_description}
      </p>
      {relatedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">{relatedItems}</div>
      )}
    </Link>
  );
}

function LetterSection({ letter, terms }) {
  var cards = terms.map(function (t) {
    return <TermCard key={t.slug} term={t} />;
  });
  return (
    <div
      id={'letter-' + letter}
      className="mb-12"
      data-testid={'glossary-section-' + letter}
    >
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-extrabold text-white shadow-md"
          style={{ background: 'linear-gradient(135deg, #2C39B8, #6FC8F5)' }}
        >
          {letter}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Letter {letter}
          </div>
          <div className="text-sm text-gray-600">
            {terms.length} term{terms.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{cards}</div>
    </div>
  );
}

function LetterNav({ letter, has, onClick }) {
  var className =
    'w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-colors ' +
    (has
      ? 'bg-[#2C39B8] text-white hover:bg-[#1F2C99] cursor-pointer'
      : 'bg-gray-100 text-gray-400 cursor-not-allowed');
  return (
    <a
      href={has ? '#letter-' + letter : undefined}
      onClick={function (e) {
        if (!has) {
          e.preventDefault();
          return;
        }
        onClick();
      }}
      className={className}
      data-testid={'glossary-letter-' + letter}
    >
      {letter}
    </a>
  );
}

const LETTERS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'];

export default function GlossaryPage() {
  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var queryState = useState('');
  var query = queryState[0];
  var setQuery = queryState[1];

  useEffect(function () {
    fetch(API_URL + '/api/glossary?limit=1000')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        setData(d);
        setLoading(false);
      })
      .catch(function (e) {
        console.error('Glossary fetch error:', e);
        setLoading(false);
      });
  }, []);

  var filtered = useMemo(function () {
    if (!data) return null;
    if (!query) return data.by_letter;
    var q = query.toLowerCase();
    var result = {};
    var keys = Object.keys(data.by_letter);
    for (var i = 0; i < keys.length; i++) {
      var letter = keys[i];
      var matches = data.by_letter[letter].filter(function (t) {
        return (
          (t.term || '').toLowerCase().indexOf(q) !== -1 ||
          (t.definition || '').toLowerCase().indexOf(q) !== -1
        );
      });
      if (matches.length > 0) result[letter] = matches;
    }
    return result;
  }, [data, query]);

  var availableLetters = useMemo(function () {
    if (!filtered) return {};
    var set = {};
    var keys = Object.keys(filtered);
    for (var i = 0; i < keys.length; i++) set[keys[i]] = true;
    return set;
  }, [filtered]);

  var totalTerms = data ? data.total : 0;
  var visibleCount = useMemo(function () {
    if (!filtered) return 0;
    var keys = Object.keys(filtered);
    var n = 0;
    for (var i = 0; i < keys.length; i++) n += filtered[keys[i]].length;
    return n;
  }, [filtered]);

  // Pre-render letter nav items
  var letterNavItems = LETTERS.map(function (l) {
    return (
      <LetterNav
        key={l}
        letter={l}
        has={!!availableLetters[l]}
        onClick={function () {}}
      />
    );
  });

  // Pre-render letter sections
  var letterSections = [];
  if (filtered) {
    for (var li = 0; li < LETTERS.length; li++) {
      var letter = LETTERS[li];
      if (filtered[letter]) {
        letterSections.push(
          <LetterSection key={letter} letter={letter} terms={filtered[letter]} />
        );
      }
    }
  }

  return (
    <>
      <SEOHead
        title="Channel Loyalty Glossary — 500+ Terms Explained | ChannelLoyalty"
        description="The definitive glossary of B2B Channel Loyalty, Dealer Loyalty, Trade Marketing and Sales Incentive terminology. 500+ terms defined with examples and strategic context."
        canonical="https://channelloyalty.ai/glossary"
      />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-24 pb-12 overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #1F2C99 0%, #2C39B8 50%, #3d4a6b 100%)',
        }}
        data-testid="glossary-hero"
      >
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute right-0 top-0 w-1/3 h-full"
            style={{
              background:
                'radial-gradient(circle at center, rgba(129, 188, 227, 0.5) 0%, transparent 70%)',
            }}
          />
        </div>
        <div className="container relative z-10 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-white/10 backdrop-blur-sm text-xs font-semibold tracking-wider uppercase">
            <BookOpen size={14} /> Authority Knowledge Hub
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight">
            Channel Loyalty Glossary
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            The definitive reference for {totalTerms || '500+'} terms across B2B Channel Loyalty,
            Dealer Loyalty, Trade Marketing, Sales Incentives, Gamification & AI-powered partner engagement.
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto relative">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              data-testid="glossary-search"
              type="search"
              placeholder="Search 500+ terms — e.g. dealer loyalty, primary sales, gamification..."
              value={query}
              onChange={function (e) { setQuery(e.target.value); }}
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-[#81bce3] shadow-xl"
            />
            {query && (
              <button
                onClick={function () { setQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 px-2"
                data-testid="glossary-search-clear"
              >
                Clear
              </button>
            )}
          </div>

          {query && (
            <p className="text-white/70 text-sm mt-3" data-testid="glossary-result-count">
              Showing <strong>{visibleCount}</strong> of {totalTerms} terms
            </p>
          )}
        </div>
      </section>

      {/* A-Z Quick Nav */}
      <section className="sticky top-16 z-20 bg-white border-b border-gray-200 py-3" data-testid="glossary-az-nav">
        <div className="container">
          <div className="flex flex-wrap gap-1.5 justify-center">{letterNavItems}</div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container">
          {loading && (
            <div className="text-center py-20 text-gray-500" data-testid="glossary-loading">
              Loading 500+ terms...
            </div>
          )}

          {!loading && filtered && Object.keys(filtered).length === 0 && (
            <div className="text-center py-20 text-gray-500" data-testid="glossary-no-results">
              No terms match &quot;{query}&quot;. Try a different search.
            </div>
          )}

          {!loading && letterSections}
        </div>
      </section>

      <ChatWidget />
      <MegaFooter />
    </>
  );
}
